import { ref, watch, nextTick, onScopeDispose, type Ref } from 'vue'

interface SamplerDeps {
  // True while the screenshot is still loading/animating — sampling is disabled then.
  screenshotAnimation: Ref<boolean>
  // Called with the sampled color string when the user commits a pick.
  onSampled?: (color: string) => void
}

// Eyedropper logic for the site screenshot: draws the image to an offscreen
// canvas and reads pixel colors under the cursor / finger, driving a magnifier.
// The `image` and `canvas` refs are returned for the host component to bind.
export function useColorSampler({ screenshotAnimation, onSampled }: SamplerDeps) {
  const canvas = ref<HTMLCanvasElement | null>(null)
  const image = ref<HTMLImageElement | null>(null)
  const imageSrc = ref<string | null>(null)

  const selectedColor = ref('#ffffff')
  const showMagnifier = ref(false)
  const magnifierX = ref(0)
  const magnifierY = ref(0)
  const screenshotPromtShow = ref(true)

  let touchTimeout: ReturnType<typeof setTimeout>
  let samplingEnabled = false // Enables color sampling after a long-press delay

  // The drawing context is kept rather than re-fetched per sample, and asks for
  // `willReadFrequently` — without it the canvas is GPU-backed and every
  // getImageData() pulls the surface back to the CPU. On a screenshot-sized
  // canvas that measured 0.71ms per read against 0.08ms here, and it happens on
  // every pointer move across the image.
  let ctx2d: CanvasRenderingContext2D | null = null

  const context = () => {
    if (ctx2d && ctx2d.canvas === canvas.value) return ctx2d
    ctx2d = canvas.value?.getContext('2d', { willReadFrequently: true }) ?? null
    return ctx2d
  }

  const getColorAt = (x: number, y: number) => {
    const ctx = context()
    if (!ctx) return '#ffffff'
    const pixel = ctx.getImageData(x, y, 1, 1).data
    return `rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]})`
  }

  // Pointer moves arrive far faster than the screen refreshes (120Hz trackpads
  // send well over one event per frame), and each one used to measure the image
  // with getBoundingClientRect, read a pixel and write four refs — a forced
  // layout and a full Vue re-render per event, on the same thread that has to
  // answer the click that samples the color. Only the newest position matters,
  // so the events just record it and one animation frame does the work.
  let queuedX = 0
  let queuedY = 0
  let queuedTouch = false
  let frame: number | null = null

  const applySample = () => {
    frame = null
    if (!canvas.value || !image.value || screenshotAnimation.value) return

    const rect = image.value.getBoundingClientRect()
    const scaleX = image.value.naturalWidth / rect.width
    const scaleY = image.value.naturalHeight / rect.height

    const x = Math.floor((queuedX - rect.left) * scaleX)
    const y = Math.floor((queuedY - rect.top) * scaleY)

    selectedColor.value = getColorAt(x, y)
    magnifierX.value = queuedX - rect.left + 15
    // Touch puts the readout above the finger; a cursor sits clear of it.
    magnifierY.value = queuedY - rect.top + (queuedTouch ? -100 : 15)
    showMagnifier.value = true
  }

  const queueSample = (clientX: number, clientY: number, touch: boolean) => {
    queuedX = clientX
    queuedY = clientY
    queuedTouch = touch
    if (frame === null) frame = requestAnimationFrame(applySample)
  }

  const cancelQueued = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame)
      frame = null
    }
  }

  const selectColor = () => {
    if (onSampled) onSampled(selectedColor.value)
  }

  const hideMagnifier = () => {
    cancelQueued()
    showMagnifier.value = false
    screenshotPromtShow.value = true
  }

  const handleMouseMove = (event: MouseEvent) => {
    if (!canvas.value || !image.value || screenshotAnimation.value) return
    if (samplingEnabled) return // Ignore if sampling is disabled

    screenshotPromtShow.value = false
    queueSample(event.clientX, event.clientY, false)
  }

  const handleTouchStart = (event: TouchEvent) => {
    samplingEnabled = false // Disable sampling initially
    touchTimeout = setTimeout(() => {
      samplingEnabled = true // Enable color sampling after 2 seconds
      handleTouchMove(event)
    }, 2000) // 2-second long-press delay
  }

  const handleTouchMove = (event: TouchEvent) => {
    if (!samplingEnabled || !canvas.value || !image.value || screenshotAnimation.value) return

    event.preventDefault()
    screenshotPromtShow.value = false

    const touch = event.touches[0]
    queueSample(touch.clientX, touch.clientY, true)
  }

  const handleTouchEnd = () => {
    clearTimeout(touchTimeout)
    // A move queued in this frame hasn't been read yet, and it is the position
    // the finger was lifted from — commit it before sampling.
    if (frame !== null) {
      cancelAnimationFrame(frame)
      applySample()
    }
    if (samplingEnabled && showMagnifier.value) {
      selectColor()
    }
    samplingEnabled = false // Reset sampling flag
    showMagnifier.value = false
    screenshotPromtShow.value = true
    hideMagnifier()
  }

  const handleClick = () => {
    // Same as touchend: the click follows the last mousemove, which may still
    // be queued for the next frame.
    if (frame !== null) {
      cancelAnimationFrame(frame)
      applySample()
    }
    selectColor() // Allow clicks normally on non-touch devices
  }

  const setupCanvas = async () => {
    await nextTick() // Ensure the DOM updates before accessing elements

    if (!image.value || !canvas.value) return

    const img = image.value
    img.crossOrigin = 'anonymous' // Avoid CORS issues

    img.onload = () => {
      // Same read-optimised context the sampling path uses; asking for it here
      // means the canvas is created CPU-backed from the start.
      const ctx = context()
      if (!ctx) {
        console.error('Canvas context is null')
        return
      }

      canvas.value!.width = img.naturalWidth
      canvas.value!.height = img.naturalHeight

      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight)
    }

    if (img.complete) {
      img.onload(new Event('load'))
    }
  }

  // Call when the screenshot URL is (re)assigned so the canvas re-initializes.
  const setDownloadedImage = (url: string) => {
    imageSrc.value = url
  }

  watch(imageSrc, (newSrc) => {
    if (newSrc) {
      setupCanvas()
    }
  })

  onScopeDispose(cancelQueued)

  return {
    canvas,
    image,
    selectedColor,
    showMagnifier,
    magnifierX,
    magnifierY,
    screenshotPromtShow,
    handleMouseMove,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleClick,
    hideMagnifier,
    setDownloadedImage,
  }
}
